import {createFeatureSelector, createSelector} from "@ngrx/store";
import {DbStoreState} from "../reducers";
import {IBuilderTemplateState} from "../reducers/builder-templates.reducer";
import {BuilderTemplatesMapper} from "../../websites/website/builder-templates/builder-templates-mapper";
import {ITenWebProductState} from "../reducers/ten-web-product.reducer";
import {IWebsiteProductState} from "../reducers/website-product.reducer";
import {getTenWebProductState} from "./ten-web-product.selector";
import {getWebsiteProductState} from "./website-product.selector";
import {Product, ProductType} from "../../websites/website/product";
import {Config} from "../../config/config";
import {ProductMapper} from "../../websites/website/product-mapper";
import {BuilderTemplates} from "../../websites/website/builder-templates/builder-templates";
import {TypeUtils} from "../../shared/utils/type-utils";

const getDbStoreState = createFeatureSelector<DbStoreState>('dbStore');

export const getBuilderTemplateState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.builderTemplates);

export const getBuilderTemplates = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) => {
    const BuilderTemplateMap: BuilderTemplates[] = Object.values(state.entities).reduce((builderTemplateMapData: any, builderTemplate: BuilderTemplates) => {
      builderTemplateMapData[builderTemplate.id] = BuilderTemplatesMapper.map(builderTemplate);
      return builderTemplateMapData;
    }, {});
    return Object.values(BuilderTemplateMap); }
);


export const getBuilderTemplatesLoading = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loading
);

export const getBuilderTemplatesLoaded = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loaded
);

export const getLastBuilderTemplate = (websiteId: number) => createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) => {
    if (!TypeUtils.isEmpty(state.lastEntities)) {
      return BuilderTemplatesMapper.map(state.lastEntities);
    }
    else {
      return null;
    }
  }
);

export const getLastBuilderTemplateLoading = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loading
);

export const getBuilderTemplatesCategoriesLoaded = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loaded
);

export const getBuilderTemplatesCategories = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) => state.categoriesEntities
);

export const getBuilderTemplatesCategoriesLoading = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loading
);

export const getLastBuilderTemplateLoaded = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loaded
);

export const getImportingBuilderTemplate = (websiteId: number) => createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) => BuilderTemplatesMapper.map(state.lastEntities)
);

export const getImportingBuilderTemplateLoading = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loading
);

export const getImportingBuilderTemplateLoaded = createSelector(getBuilderTemplateState,
  (state: IBuilderTemplateState) =>
    state.loaded
);

export const getWebsiteTenWebTheme = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: { [slug: string]: any } = tenWebProductState.entities;
    const websiteThemesMap: { [slug: string]: any } = websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].themeEntities : {};
    const websiteTenWebTheme: Product = tenWebProductsMap && tenWebProductsMap[Config.tenWebThemeSlug]  ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.tenWebThemeSlug], websiteThemesMap[Config.tenWebThemeSlug]) : null;
    return websiteTenWebTheme;
  }
);

export const getWebsiteWvcTheme = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
    (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
        const tenWebProductsMap: { [slug: string]: any } = tenWebProductState.entities;
        const websiteThemesMap: { [slug: string]: any } = websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].themeEntities : {};
        const websiteWvcTheme: Product = tenWebProductsMap && tenWebProductsMap[Config.wvcThemeSlug]  ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.wvcThemeSlug], websiteThemesMap[Config.wvcThemeSlug]) : null;
        return websiteWvcTheme;
    }
);

export const getWebsiteBuilderPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: { [slug: string]: any } = tenWebProductState.entities;
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteBuilderPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.builderPluginSlug] && !TypeUtils.isEmpty(websitePluginsMap) ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.builderPluginSlug], websitePluginsMap[Config.builderPluginSlug]) : null;
    return websiteBuilderPlugin;
  }
);

export const getWebsiteElementorPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteElementorPlugin: Product = websitePluginsMap[Config.elementorPluginSlug] ? ProductMapper.mapWordpressProduct(websitePluginsMap[Config.elementorPluginSlug], ProductType.PLUGIN) : null;
    return websiteElementorPlugin;
  }
);

export const getWebsiteEcommerceTaxPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteTaxPlugin: Product = websitePluginsMap[Config.woocommerceTaxPluginSlug] ? ProductMapper.mapWordpressProduct(websitePluginsMap[Config.woocommerceTaxPluginSlug], ProductType.PLUGIN) : null;
    return websiteTaxPlugin;
  }
);
